import { parseISO, parse, isValid, setISOWeek, startOfISOWeek } from 'date-fns';
import { formatDateCategory } from '../../components/CustomDashboard/WidgetRenderer/echartsUtils';
import type { SocialGraphEdgeDTO, CaseEdgeDTO } from '../../utils/socialGraphApi';

// Series hues from the timeline redesign (Figma Timeline / Legend item 502-11)
export const MENTION_TYPE_COLORS: Record<string, string> = {
  MENTION: '#88A8EC',
  REPOST: '#E8739F',
  LINK: '#6FCF97',
};

export const MENTION_TYPE_LABELS: Record<string, string> = {
  MENTION: 'Mentions',
  REPOST: 'Reposts',
  LINK: 'Links',
};

// Translated interaction-type label. The 'interaction' context gives "Links"
// its own uk translation (Посилання — hyperlinks) separate from the generic
// 'Links' key other pages use for connections (Звʼязки); i18next falls back to
// the plain key where no `_interaction` variant exists (Mentions / Reposts)
export const mentionTypeLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  type: string,
): string => t(MENTION_TYPE_LABELS[type] ?? type, { context: 'interaction' });

export interface TimelineDataPoint {
  key: string;
  date: Date;
  label: string;
  byType: Record<string, number>;
}

export function parseTimeBucketKey(key: string): Date {
  const weekMatch = key.match(/^(\d{4})-W(\d{2})$/);
  if (weekMatch) {
    return startOfISOWeek(setISOWeek(new Date(Number(weekMatch[1]), 0, 4), Number(weekMatch[2])));
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(key)) {
    const d = parseISO(key);
    if (isValid(d)) return d;
  }

  if (/^\d{4}-\d{2}$/.test(key)) {
    const d = parse(key, 'yyyy-MM', new Date());
    if (isValid(d)) return d;
  }

  const fallback = new Date(key);
  if (!isValid(fallback)) {
    console.warn(`Unrecognized time bucket key: ${key}`);
  }
  return fallback;
}

export function aggregateTimeline(edges: SocialGraphEdgeDTO[]): TimelineDataPoint[] {
  const buckets = new Map<string, Record<string, number>>();

  for (const edge of edges) {
    if (!edge.time_buckets) continue;
    const mentionTypes = edge.mention_types ?? {};
    const typeTotal = Object.values(mentionTypes).reduce((s, v) => s + v, 0) || 1;

    for (const [bucketKey, bucketCount] of Object.entries(edge.time_buckets)) {
      let byType = buckets.get(bucketKey);
      if (!byType) {
        byType = {};
        buckets.set(bucketKey, byType);
      }

      for (const [type, typeCount] of Object.entries(mentionTypes)) {
        const share = Math.round(bucketCount * (typeCount / typeTotal));
        byType[type] = (byType[type] ?? 0) + share;
      }
    }
  }

  const points: TimelineDataPoint[] = [];
  for (const [key, byType] of buckets) {
    points.push({
      key,
      date: parseTimeBucketKey(key),
      label: formatDateCategory(key),
      byType,
    });
  }

  points.sort((a, b) => a.date.getTime() - b.date.getTime());
  return points;
}

// Which edges are active in a period is answered by timelineIndex.ts (parsed
// once per dataset, keyed by graphology edge keys); this module only shapes the
// chart series.

export function aggregateCaseTimeline(edges: CaseEdgeDTO[]): TimelineDataPoint[] {
  const buckets = new Map<string, number>();

  for (const edge of edges) {
    if (!edge.time_buckets) continue;
    for (const [bucketKey, count] of Object.entries(edge.time_buckets)) {
      buckets.set(bucketKey, (buckets.get(bucketKey) ?? 0) + count);
    }
  }

  const points: TimelineDataPoint[] = [];
  for (const [key, count] of buckets) {
    points.push({
      key,
      date: parseTimeBucketKey(key),
      label: formatDateCategory(key),
      byType: { REPOST: count },
    });
  }

  points.sort((a, b) => a.date.getTime() - b.date.getTime());
  return points;
}
